import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { MePhoneVerification, maskPhone } from '@shared/components/MePhoneVerification';
import { useAuth } from '@shared/state/hooks/useAuth';
import { Gender } from '@shared/types';
import { Alert, Card, Descriptions, Input, Modal, Select, Tag, message } from 'antd';

interface InfoForm {
  name: string;
  phoneNumber: string;
  gender: string;
  birth: string;
}

const emptyInfoForm: InfoForm = {
  name: '',
  phoneNumber: '',
  gender: '',
  birth: '',
};

//* 전화번호 하이픈 포맷 (숫자만 추출 후 010-1234-5678 형태)
const formatPhoneNumber = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length < 4) return digits;
  if (digits.length < 8) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
};

const genderLabel = (gender: string) => {
  if (gender === Gender.Male) return '남';
  if (gender === Gender.Femail) return '여';
  return '-';
};

interface Props {
  //* 상단 타이틀바가 없는 화면(gift-seller 등)에서 페이지 제목을 직접 표시할 때 사용
  heading?: string;
}

export const AccountSettings: FC<Props> = function AccountSettings({ heading }: Props) {
  const {
    userProfile,
    patchMe,
    patchMePassword,
    verifyCurrentPassword,
    refreshUserProfile,
  } = useAuth();

  // ─── 기본정보 (보기 → 수정 토글) ───
  const [editing, setEditing] = useState(false);
  const [infoForm, setInfoForm] = useState<InfoForm>(emptyInfoForm);
  const [infoSaving, setInfoSaving] = useState(false);

  const profileInfo = useMemo<InfoForm>(
    () => ({
      name: userProfile?.info?.name ?? '',
      phoneNumber: userProfile?.info?.phoneNumber ?? '',
      gender: userProfile?.info?.gender ?? '',
      birth: userProfile?.info?.birth ?? '',
    }),
    [userProfile],
  );

  // 편집모드 진입 시 현재 값으로 폼 초기화
  useEffect(() => {
    if (!editing) setInfoForm(profileInfo);
  }, [editing, profileInfo]);

  const infoDirty = useMemo(
    () =>
      infoForm.name !== profileInfo.name ||
      infoForm.phoneNumber !== profileInfo.phoneNumber ||
      infoForm.gender !== profileInfo.gender ||
      infoForm.birth !== profileInfo.birth,
    [infoForm, profileInfo],
  );

  const handleStartEdit = useCallback(() => {
    setInfoForm(profileInfo);
    setEditing(true);
  }, [profileInfo]);

  const handleCancelEdit = useCallback(() => {
    setInfoForm(profileInfo);
    setEditing(false);
  }, [profileInfo]);

  const handleSaveInfo = useCallback(async () => {
    if (!infoForm.name.trim()) {
      message.warning('이름을 입력해주세요.');
      return;
    }
    if (!userProfile?.info) return;
    if (!infoDirty) {
      setEditing(false);
      return;
    }
    setInfoSaving(true);
    try {
      const updated = await patchMe({
        info: {
          ...userProfile.info,
          name: infoForm.name.trim(),
          phoneNumber: infoForm.phoneNumber.trim(),
          gender: infoForm.gender,
          birth: infoForm.birth.trim(),
        },
      });
      if (updated) {
        await refreshUserProfile();
        message.success('기본정보가 저장되었습니다.');
        setEditing(false);
      } else {
        message.error('기본정보 저장에 실패했습니다.');
      }
    } finally {
      setInfoSaving(false);
    }
  }, [infoForm, infoDirty, patchMe, refreshUserProfile, userProfile]);

  // ─── 비밀번호 변경 (현재 비밀번호 확인 모달 → 새 비밀번호 입력) ───
  const [pwModalOpen, setPwModalOpen] = useState(false);
  const [pwVerifying, setPwVerifying] = useState(false);
  const [pwUnlocked, setPwUnlocked] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  //* 현재 비밀번호를 모를 때 — 등록 휴대폰 인증으로 대신 연다. proof 는 PATCH 에 동봉
  const [pwViaPhone, setPwViaPhone] = useState(false);
  const [phoneProof, setPhoneProof] = useState<string | null>(null);
  const registeredPhoneMasked = maskPhone(userProfile?.info?.phoneNumber);
  const weakPassword = userProfile?.security?.isWeakPassword === true;

  const openPwModal = useCallback(() => {
    setCurrentPassword('');
    setPwViaPhone(false);
    setPwModalOpen(true);
  }, []);

  const handlePhoneVerified = useCallback((proof: string) => {
    setPhoneProof(proof);
    setCurrentPassword('');
    setPwUnlocked(true);
    setPwModalOpen(false);
    setPwViaPhone(false);
  }, []);

  // 현재 비밀번호 검증 → 성공해야 새 비밀번호 입력 활성화
  const handleVerifyCurrent = useCallback(async () => {
    if (!currentPassword) {
      message.warning('현재 비밀번호를 입력해주세요.');
      return;
    }
    setPwVerifying(true);
    try {
      const result = await verifyCurrentPassword(currentPassword);
      if (!result.verified) {
        //* 남은 횟수·기기 잠금 안내가 실려 온다 (이 확인도 로그인 실패로 집계된다)
        message.error(result.message ?? '현재 비밀번호가 일치하지 않습니다.', result.locked ? 8 : undefined);
        return;
      }
      setPwUnlocked(true);
      setPwModalOpen(false);
    } finally {
      setPwVerifying(false);
    }
  }, [currentPassword, verifyCurrentPassword]);

  const handleCancelPwChange = useCallback(() => {
    setPwUnlocked(false);
    setCurrentPassword('');
    setPhoneProof(null);
    setPassword('');
    setPasswordConfirm('');
  }, []);

  const handleChangePassword = useCallback(async () => {
    if (password.length < 4 || password.length > 16) {
      message.warning('새 비밀번호는 4~16자리로 입력해주세요.');
      return;
    }
    if (password !== passwordConfirm) {
      message.warning('새 비밀번호와 비밀번호 확인이 일치하지 않습니다.');
      return;
    }
    if (currentPassword && password === currentPassword) {
      message.warning('현재 비밀번호와 다른 비밀번호를 입력해주세요.');
      return;
    }
    setPwSaving(true);
    try {
      const ok = await patchMePassword(password, {
        currentPassword: currentPassword || undefined,
        phoneProof: phoneProof ?? undefined,
      });
      if (ok) {
        handleCancelPwChange();
        //* 취약 판정이 서버에서 갱신되므로 프로필을 다시 읽어 배지·안내를 지운다
        await refreshUserProfile();
      }
    } finally {
      setPwSaving(false);
    }
  }, [
    password,
    passwordConfirm,
    currentPassword,
    phoneProof,
    patchMePassword,
    handleCancelPwChange,
    refreshUserProfile,
  ]);

  return (
    <div className="mx-auto max-w-4xl px-4 pb-8 sm:px-6 lg:px-8">
      <div className="space-y-6 py-6">
        {heading && (
          <h1 className="text-xl font-bold text-gray-800">{heading}</h1>
        )}

        {/* ─── 계정 정보 (읽기 전용) ─── */}
        <Card title="계정 정보">
          <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
            <Descriptions.Item label="아이디">
              {userProfile?.username ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="가입일">
              {userProfile?.createdAt
                ? new Date(userProfile.createdAt).toLocaleDateString('ko-KR')
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="권한" span={2}>
              <div className="flex flex-wrap gap-1">
                {(userProfile?.roles ?? []).map((role) => (
                  <Tag key={role}>{role}</Tag>
                ))}
              </div>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* ─── 기본정보 (보기 → 수정 토글) ─── */}
        <Card
          title="기본정보"
          extra={
            editing ? (
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={infoSaving}
                  className="rounded border border-gray-300 bg-white px-3 py-1 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                  onClick={handleCancelEdit}
                >
                  취소
                </button>
                <button
                  type="button"
                  disabled={infoSaving || !infoDirty}
                  className="rounded px-3 py-1 text-sm font-medium text-white theme-bg-main hover:opacity-90 disabled:opacity-50"
                  onClick={handleSaveInfo}
                >
                  저장
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="rounded border bg-white px-3 py-1 text-sm theme-boder-main theme-text-main hover:theme-bg-5"
                onClick={handleStartEdit}
              >
                수정
              </button>
            )
          }
        >
          {editing ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium">이름</label>
                <Input
                  placeholder="이름"
                  value={infoForm.name}
                  onChange={(e) =>
                    setInfoForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">전화번호</label>
                <Input
                  placeholder="010-1234-5678"
                  value={infoForm.phoneNumber}
                  onChange={(e) =>
                    setInfoForm((prev) => ({
                      ...prev,
                      phoneNumber: formatPhoneNumber(e.target.value),
                    }))
                  }
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">성별</label>
                <Select
                  className="w-full"
                  placeholder="성별 선택"
                  allowClear
                  value={infoForm.gender || undefined}
                  onChange={(val) =>
                    setInfoForm((prev) => ({ ...prev, gender: val ?? '' }))
                  }
                  options={[
                    { value: Gender.Male, label: '남' },
                    { value: Gender.Femail, label: '여' },
                  ]}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">생년월일</label>
                <input
                  type="date"
                  className="ant-input w-full rounded border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:theme-boder-main"
                  value={infoForm.birth}
                  onChange={(e) =>
                    setInfoForm((prev) => ({ ...prev, birth: e.target.value }))
                  }
                />
              </div>
            </div>
          ) : (
            <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
              <Descriptions.Item label="이름">
                {profileInfo.name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="전화번호">
                {profileInfo.phoneNumber || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="성별">
                {genderLabel(profileInfo.gender)}
              </Descriptions.Item>
              <Descriptions.Item label="생년월일">
                {profileInfo.birth || '-'}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Card>

        {/* ─── 비밀번호 변경 ─── */}
        <Card
          title={
            <span className="flex items-center gap-2">
              비밀번호 변경
              {weakPassword && (
                <span className="rounded-full bg-red-500 px-2 py-0.5 text-xs font-bold text-white">
                  취약
                </span>
              )}
            </span>
          }
          extra={
            pwUnlocked ? (
              <button
                type="button"
                disabled={pwSaving}
                className="rounded border border-gray-300 bg-white px-3 py-1 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                onClick={handleCancelPwChange}
              >
                취소
              </button>
            ) : (
              <button
                type="button"
                className="rounded border bg-white px-3 py-1 text-sm theme-boder-main theme-text-main hover:theme-bg-5"
                onClick={openPwModal}
              >
                비밀번호 변경
              </button>
            )
          }
        >
          {weakPassword && (
            <Alert
              type="warning"
              showIcon
              className="mb-4"
              message="비밀번호가 취약합니다"
              description={
                <span className="text-13 leading-5">
                  기본값(1234 등)·연속·반복 숫자·아이디·전화번호와 같은 값은 피해서 바꿔 주세요.
                  현재 비밀번호를 모르시면 휴대폰 인증으로 바꿀 수 있습니다.
                </span>
              }
            />
          )}
          {pwUnlocked ? (
            <div className="space-y-4">
              {phoneProof && (
                <p className="text-xs text-gray-500">휴대폰 인증으로 확인되었습니다. 새 비밀번호를 입력하세요.</p>
              )}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium">새 비밀번호</label>
                  <Input.Password
                    placeholder="4~16자리"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">
                    새 비밀번호 확인
                  </label>
                  <Input.Password
                    placeholder="새 비밀번호 확인"
                    autoComplete="new-password"
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    onPressEnter={handleChangePassword}
                  />
                </div>
              </div>
              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  disabled={pwSaving || !password || !passwordConfirm}
                  className="rounded px-4 py-2 text-sm font-medium text-white theme-bg-main hover:opacity-90 disabled:opacity-50"
                  onClick={handleChangePassword}
                >
                  변경 완료
                </button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">
              비밀번호를 변경하려면 우측 상단 ‘비밀번호 변경’을 눌러 현재 비밀번호를
              확인하세요.
            </p>
          )}
        </Card>
      </div>

      {/* ─── 현재 비밀번호 확인 모달 ─── */}
      <Modal
        title={pwViaPhone ? '휴대폰 인증' : '현재 비밀번호 확인'}
        open={pwModalOpen}
        onOk={handleVerifyCurrent}
        onCancel={() => setPwModalOpen(false)}
        confirmLoading={pwVerifying}
        okText="확인"
        cancelText="취소"
        okButtonProps={{
          style: { backgroundColor: '#2563eb', borderColor: '#2563eb', color: '#fff' },
        }}
        //* 휴대폰 인증 단계는 자기 버튼으로 진행한다 — 모달 기본 footer 는 숨긴다
        footer={pwViaPhone ? null : undefined}
        destroyOnClose
      >
        {pwViaPhone ? (
          <MePhoneVerification
            phoneMasked={registeredPhoneMasked}
            onVerified={handlePhoneVerified}
            onCancel={() => setPwViaPhone(false)}
          />
        ) : (
          <div className="space-y-2 py-2">
            <label className="block text-sm font-medium">현재 비밀번호</label>
            <Input.Password
              placeholder="현재 비밀번호를 입력하세요"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              onPressEnter={handleVerifyCurrent}
            />
            <p className="text-xs text-gray-400">
              현재 비밀번호가 일치해야 새 비밀번호를 입력할 수 있습니다. 여러 번 틀리면 이
              기기에서 로그인이 잠깁니다.
            </p>
            {registeredPhoneMasked ? (
              <button
                type="button"
                className="text-xs underline theme-text-main"
                onClick={() => setPwViaPhone(true)}
              >
                현재 비밀번호를 모르시나요? 등록된 휴대폰({registeredPhoneMasked})으로 인증
              </button>
            ) : (
              <p className="text-xs text-gray-400">
                등록된 휴대폰번호가 없어 인증으로는 바꿀 수 없습니다. 모르시면 관리자에게 문의해
                주세요.
              </p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};
